import * as React from "react";

import { cn } from "../../lib/utils";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import {
  chartTooltipContainerStyle,
  chartTooltipLabelStyle,
} from "./chart-theme";

/**
 * ChartTooltipShell — shared Recharts tooltip chrome (issue #234, DESIGN-SPEC
 * §4.5: "tooltip on hover (surface bg, border, mono values)"; §6: "tooltip
 * fade 80ms").
 *
 * This renders the surface/border/radius chrome and an optional mono label
 * row; pass row content (label + mono value pairs) as `children`, typically
 * built with `ChartTooltipRow`. Individual charts keep their own
 * `payload`-parsing logic (series names, formatting) and pass a
 * `content={(props) => <ChartTooltipShell label={...}>{...}</ChartTooltipShell>}`
 * to Recharts' `<Tooltip>` — this module only owns cross-chart chrome, not
 * per-chart data shape, so it composes with any existing chart's payload
 * mapping without forcing a rewrite.
 *
 * `active`/`payload` mirror Recharts' `<Tooltip content>` render-prop
 * signature; when `active` is false (or payload is empty and
 * `allowEmptyPayload` is not set) nothing is rendered, matching Recharts'
 * default tooltip behavior.
 *
 * Reduced motion (spec §6/§7 "prefers-reduced-motion: disable ... any
 * transitions"): the 80ms fade is skipped and the tooltip renders at full
 * opacity immediately.
 */
export function ChartTooltipShell({
  active,
  payload,
  label,
  children,
  className,
  allowEmptyPayload = false,
  ...props
}) {
  const prefersReducedMotion = usePrefersReducedMotion();

  if (!active || (!allowEmptyPayload && (!payload || payload.length === 0))) {
    return null;
  }

  return (
    <div
      className={cn(
        "font-sans",
        // 80ms fade (spec §4.5/§6). Skipped entirely under
        // prefers-reduced-motion (§7) — tailwindcss-animate's `animate-in`
        // utility (already used by dialog.jsx/select.jsx) drives the actual
        // fade; duration is set via the `duration-[80ms]` arbitrary-value
        // class rather than inline `transitionDuration` for consistency with
        // button.jsx's `duration-[120ms]` convention (issue #233).
        !prefersReducedMotion && "animate-in fade-in duration-[80ms]",
        className
      )}
      style={chartTooltipContainerStyle}
      {...props}
    >
      {label !== undefined && label !== null && (
        <div style={chartTooltipLabelStyle}>{label}</div>
      )}
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}
ChartTooltipShell.displayName = "ChartTooltipShell";

/**
 * ChartTooltipRow — one label/value line inside a ChartTooltipShell.
 * `swatchColor` renders a small line/dot matching the series color (never
 * required — donut/bar tooltips may omit it).
 */
export function ChartTooltipRow({ label, value, swatchColor, className }) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 text-[11px]",
        className
      )}
    >
      <span className="flex items-center gap-1.5 text-text-faint">
        {swatchColor && (
          <span
            className="h-[2px] w-3 shrink-0 rounded-full"
            style={{ backgroundColor: swatchColor }}
            aria-hidden="true"
          />
        )}
        {label}
      </span>
      <span className="font-mono tabular-nums text-foreground">{value}</span>
    </div>
  );
}
ChartTooltipRow.displayName = "ChartTooltipRow";

export default ChartTooltipShell;
